// The switch that decides whether unsigned context is refused, behind Edit
// and Save like every other section.
//
// Deliberately separate from "a key exists": adding a key must not by
// itself start rejecting traffic, or an integration could never be migrated
// — every embed would have to switch over in the same instant. So the
// sequence is add the key, watch responses start verifying, then flip this.
//
// The number beside it is doing the real work. Turning this on can refuse
// live traffic, and no amount of switch styling tells you whether that is
// safe; the count of context that arrived unsigned does.

import { Badge, Button, Confirm, Switch, toast } from "@pej/kore/components";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { usePatchSecurity } from "@/api/security";
import type { SecuritySettings } from "@/api/types";
import { toastMutationError } from "@/components/error-toast";
import { SectionCard } from "@/components/section-page/section-card";
import { useSectionEdit } from "@/components/section-page/section-edit";
import { countOf } from "@/lib/plural";
import { SECURITY_SECTION } from "./security-sections";

export interface EnforcementSectionProps {
    workspaceId: string;
    settings: SecuritySettings;
}

export function EnforcementSection({ workspaceId, settings }: EnforcementSectionProps) {
    const saved = settings.requireSignedContext;
    const hasKeys = settings.keys.length > 0;
    const [next, setNext] = useState(saved);
    const [confirming, setConfirming] = useState(false);
    const needsKey = !hasKeys && !saved;
    const patch = usePatchSecurity(workspaceId);
    const edit = useSectionEdit({
        sectionId: SECURITY_SECTION.enforcement,
        sectionTitle: "Enforcement",
        isDirty: next !== saved,
        reset: () => setNext(saved),
    });
    // Follow the saved value while not editing.
    useEffect(() => {
        if (!edit.isEditing) setNext(saved);
    }, [saved, edit.isEditing]);

    async function apply(value: boolean) {
        try {
            await patch.mutateAsync(value);
            toast.success(value ? "Unsigned context will be refused" : "Unsigned context is accepted again");
            edit.exitEdit();
        } catch (err) {
            toastMutationError("Save failed", err);
        } finally {
            setConfirming(false);
        }
    }

    function save() {
        // Turning it on can refuse live traffic, so it asks first; off only relaxes.
        if (next) setConfirming(true);
        else void apply(false);
    }

    function cancel() {
        setNext(saved);
        edit.exitEdit();
    }

    const actions = edit.isEditing ? (
        <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={cancel}>
                Cancel
            </Button>
            <Button type="button" size="sm" disabled={next === saved || patch.isPending} onClick={save}>
                Save
            </Button>
        </div>
    ) : (
        // With no key (and so nothing to turn off) there is nothing to edit:
        // the reason shows below rather than in a dead-end editor.
        <Button type="button" variant="outline" size="sm" disabled={needsKey} onClick={edit.enterEdit}>
            <Pencil aria-hidden="true" />
            Edit
        </Button>
    );

    return (
        <SectionCard id={SECURITY_SECTION.enforcement} title="Enforcement" actions={actions}>
            <div className="flex flex-col gap-3 text-sm">
                <div className="flex items-center justify-between gap-6">
                    <p className="text-kore-text-muted">
                        When on, an embedding system must sign the guest and venue it sends. When off, unsigned context
                        is still accepted and recorded as unverified.
                    </p>
                    {edit.isEditing ? (
                        <Switch
                            checked={next}
                            disabled={!hasKeys || patch.isPending}
                            aria-label="Refuse unsigned context"
                            onCheckedChange={setNext}
                        />
                    ) : (
                        <Badge tone={saved ? "success" : "neutral"}>{saved ? "On" : "Off"}</Badge>
                    )}
                </div>
                {needsKey && (
                    <p className="text-kore-text-muted" data-testid="needs-key">
                        Add a public key first. With nothing to verify against, this would refuse every integration at
                        once.
                    </p>
                )}
                {hasKeys && !saved && (
                    <p data-testid="unsigned-recently">
                        {settings.unsignedRecently === 0
                            ? "Nothing has sent unsigned context in the last 30 days, so turning this on should refuse nothing."
                            : `${countOf(settings.unsignedRecently, "response")} in the last 30 days carried unsigned context. Turning this on would have refused ${settings.unsignedRecently === 1 ? "it" : "them"}.`}
                    </p>
                )}
            </div>
            <Confirm
                open={confirming}
                onOpenChange={(open) => {
                    if (!open && !patch.isPending) setConfirming(false);
                }}
                title="Refuse unsigned context?"
                description={
                    settings.unsignedRecently === 0
                        ? "Nothing has sent unsigned context recently, so this should refuse nothing. You can turn it off again at any time."
                        : `${countOf(settings.unsignedRecently, "response")} in the last 30 days would have been refused. Anything still sending unsigned context stops working until it signs.`
                }
                primaryLabel={patch.isPending ? "Turning on…" : "Turn on"}
                confirming={patch.isPending}
                onConfirm={() => void apply(true)}
            />
        </SectionCard>
    );
}
